import React, { forwardRef, useState } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { NudgeSpotlight } from '@atlassiansox/nudge-tooltip';
import Link from '@atlaskit/link';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { HR_SERVICE_PROJECT_TEAM_MANAGED_KEY } from '@atlassian/jira-common-constants/src/project-template-type-keys.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectCreateDrawer } from '@atlassian/jira-project-create-drawer-context/src/index.tsx';
import { useSeenStatus } from '@atlassian/jira-servicedesk-itsm-onboarding-common/src/controllers/seen-status/index.tsx';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { createOnboardingSpotlight } from '@atlassian/jira-software-onboarding-nudges--next/src/common/ui/onboarding-spotlight/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import spotlightImage from '../common/assets/spotlightImage.png';
import { JSM_HR_EXPANSION_SPOTLIGHT_MESSAGE } from '../common/constants.tsx';
import messages from '../messages.tsx';

export const HrExpansionSpotlightView = (): React.JSX.Element | null => {
	const [showSpotlight, setShowSpotlight] = useState<boolean>(true);
	const JSM_HR_EXPANSION_SPOTLIGHT_SEEN_STATUS_USER_PROPERTY_KEY =
		'JsmHRExpansionExperimentSpotlightSeenStatus';
	const [{ isSeen: hasSeenSpotlight }, { markAsSeen: markSpotlightAsSeen }] = useSeenStatus({
		userPropertyKey: JSM_HR_EXPANSION_SPOTLIGHT_SEEN_STATUS_USER_PROPERTY_KEY,
		isSeenValue: true,
		packageName: 'jsmHrExpansionExperiment',
		teamName: 'jsm-getting-started',
	});
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ data: pageContextData }] = usePageContext();
	const {
		methods: { open: openProjectCreate },
	} = useProjectCreateDrawer();

	const { projectId, projectType } = pageContextData?.projectContext || {};

	const renderSpotlightCard = () => {
		const onDismissSpotlight = () => {
			setShowSpotlight(false);
			markSpotlightAsSeen();
		};

		const handleTryNow = () => {
			onDismissSpotlight();
			markSpotlightAsSeen();
			openProjectCreate({
				defaultSelectedTemplateKey: HR_SERVICE_PROJECT_TEAM_MANAGED_KEY,
			});
		};

		const handleLearnMore = () => {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'HrExpansionSpotlightLearnMore', {
				projectId,
				projectType,
			});
		};

		const LEARN_MORE_LINK = 'https://www.atlassian.com/software/jira/service-management/hr';

		const renderNudge = () => {
			const SpotlightRef = createOnboardingSpotlight({
				actions: [
					{
						onClick: () => {
							fireUIAnalytics(
								createAnalyticsEvent({}),
								'button clicked',
								'HrExpansionSpotlightDismiss',
								{
									projectId,
									projectType,
								},
							);
							onDismissSpotlight();
						},
						content: formatMessage(messages.dismiss),
						appearance: 'subtle',
					},
					{
						onClick: () => {
							fireUIAnalytics(
								createAnalyticsEvent({}),
								'button clicked',
								'HrExpansionSpotlightActivate',
								{
									projectId,
									projectType,
								},
							);
							handleTryNow();
						},
						content: formatMessage(messages.enable),
						appearance: 'primary',
					},
				],
				contentMessage: (
					<>
						{formatMessage(
							fg('itsm-project-renaming') ? messages.descriptionGalaxia : messages.description,
							{
								a: (chunks: React.ReactNode[]) => (
									<Link href={LEARN_MORE_LINK} target="_blank" onClick={handleLearnMore}>
										{chunks}
									</Link>
								),
							},
						)}
					</>
				),

				image: <img src={spotlightImage} alt="" width="320" />,
				headingMessage: formatMessage(messages.heading),
			});

			// eslint-disable-next-line react/no-unstable-nested-components
			return forwardRef<HTMLDivElement>((_, ref) => (
				<div ref={ref}>
					<SpotlightRef />
				</div>
			));
		};

		return (
			<ModalWrapperStyles>
				<ContextualAnalyticsData sourceType={SCREEN} sourceName="JsmHrExpansionSpotlight">
					{/* @ts-expect-error TS2741: Property 'children' is missing in type */}
					<NudgeSpotlight
						setHidden={noop}
						hidden={!showSpotlight}
						spotlightCard={renderNudge()}
						position="bottom-end"
						zIndex={layers.spotlight()}
						hideSpotlightCardOnOutsideClick={false}
					/>
					<FireScreenAnalytics
						attributes={{
							messageId: JSM_HR_EXPANSION_SPOTLIGHT_MESSAGE,
							name: 'JsmHrExpansionSpotlight',
							projectId,
							projectType,
						}}
					/>
				</ContextualAnalyticsData>
			</ModalWrapperStyles>
		);
	};

	return showSpotlight && !hasSeenSpotlight ? renderSpotlightCard() : null;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalWrapperStyles = styled.div({
	position: 'absolute',
	bottom: token('space.400'),
	right: token('space.600'),
});
